import { ProjectModel } from '@project/model/project.model';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { PropertyValueVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyTypes } from '../components/property/properties-table/property-types.enum';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';
import { AdminService } from '../services/admin.service';

export class BasePropertyOperation {

  constructor(
    private _property: PropertyVO,
    private _propertyType: string,
    private projectModel: ProjectModel,
    protected connectionUtils: ConnectionUtils,
    private adminService: AdminService
  ) {}

  public addConnectionPropertyToConnection(connectionName: string, article: ArticleVO): void {
    const connectionArea: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionName);
    this.addConnectionProperty(this._property, this._propertyType, connectionArea);
    this.connectionUtils.updateInternalConnectionPropertiesByName(connectionName, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  public removeConnectionPropertyFromConnection(connectionName: string, article: ArticleVO): void {
    const connectionArea: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionName);
    this.deleteConnectionProperty(this._property, this._propertyType, connectionArea);
    this.connectionUtils.updateInternalConnectionPropertiesByName(connectionName, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  public addArticlePropertyToArticle(article: ArticleVO): void {
    this.addArticleProperty(this._property, this._propertyType, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  public removeArticlePropertyFromArticle(article: ArticleVO): void {
    this.deleteArticleProperty(this._property, this._propertyType, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  // Property changes

  public undoConnectionPropertyChanges(property: PropertyVO, propertyType: string, connectionName: string, article: ArticleVO): void {
    const connectionArea: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionName);

    this.deleteConnectionProperty(property, propertyType, connectionArea);
    this.addConnectionProperty(this._property, this._propertyType, connectionArea);

    this.connectionUtils.updateInternalConnectionPropertiesByName(connectionName, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  public redoConnectionPropertyChanges(property: PropertyVO, propertyType: string, connectionName: string, article: ArticleVO): void {
    const connectionArea: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionName);

    this.deleteConnectionProperty(this._property, this._propertyType, connectionArea);
    this.addConnectionProperty(property, propertyType, connectionArea);

    this.connectionUtils.updateInternalConnectionPropertiesByName(connectionName, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  public undoArticlePropertyChanges(property: PropertyVO, propertyType: string, article: ArticleVO): void {
    this.deleteArticleProperty(property, propertyType, article);
    this.addArticleProperty(this._property, this._propertyType, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  public redoArticlePropertyChanges(property: PropertyVO, propertyType: string, article: ArticleVO): void {
    this.deleteArticleProperty(this._property, this._propertyType, article);
    this.addArticleProperty(property, propertyType, article);

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }

  private deleteArticleProperty(property: PropertyVO, propertyType: string, article: ArticleVO): void {
    if (article) {
      if (propertyType === PropertyTypes.BEHAVIOR) {
        this.removeProperty(property, article.behaviorProperties);
      } else if (propertyType === PropertyTypes.PLAUSI) {
        this.removeProperty(property, article.plausiProperties);
      } else if (propertyType === PropertyTypes.GIVING) {
        this.removeProperty(property, article.connectionFeatures);
      } else if (propertyType === PropertyTypes.ACCEPTING) {
        this.removeProperty(property, article.connectionRequirements);
      }
    }
  }

  private deleteConnectionProperty(property: PropertyVO, propertyType: string, connectionArea: MetaElementVO): void {
    if (connectionArea) {
      if (propertyType === PropertyTypes.BEHAVIOR) {
        this.removeProperty(property, connectionArea.behaviorProperties);
      } else if (connectionArea instanceof ConnectionAreaVO) {
        if (propertyType === PropertyTypes.PLAUSI) {
          this.removeProperty(property, connectionArea.connectionPlausiProperties);
        } else if (propertyType === PropertyTypes.GIVING) {
          this.removeProperty(property, connectionArea.connectionFeatures);
        } else if (propertyType === PropertyTypes.ACCEPTING) {
          this.removeProperty(property, connectionArea.connectionRequirements);
        }
      }
    }
  }

  private removeProperty(property: PropertyVO, source: PropertyVO[]): void {
    const propertyIndex = source.findIndex((p) => p.name === property.name);
    if (propertyIndex >= 0) {
      source.splice(propertyIndex, 1);
    }
    this.adminService.updateProperties([property.name]);
  }

  private addArticleProperty(property: PropertyVO, propertyType: string, article: ArticleVO): void {
    if (article) {
      if (propertyType === PropertyTypes.BEHAVIOR) {
        article.behaviorProperties.push(property);
      } else if (propertyType === PropertyTypes.PLAUSI) {
        article.plausiProperties.push(property);
      } else if (propertyType === PropertyTypes.GIVING) {
        article.connectionFeatures.push(property);
      } else if (propertyType === PropertyTypes.ACCEPTING) {
        article.connectionRequirements.push(property);
      }
    }
  }

  private addConnectionProperty(property: PropertyVO, propertyType: string, connectionArea: MetaElementVO): void {
    if (connectionArea) {
      if (propertyType === PropertyTypes.BEHAVIOR) {
        connectionArea.behaviorProperties.push(property);
      } else if (connectionArea instanceof ConnectionAreaVO) {
        if (propertyType === PropertyTypes.PLAUSI) {
          connectionArea.connectionPlausiProperties.push(property);
        } else if (propertyType === PropertyTypes.GIVING) {
          connectionArea.connectionFeatures.push(property);
        } else if (propertyType === PropertyTypes.ACCEPTING) {
          connectionArea.connectionRequirements.push(property);
        }
      }
    }
  }

  public changePropertyValues(values: PropertyValueVO[]): void {
    this._property.values = values;

    this.adminService.updateProperties([this._property.name]);
    this.projectModel.notifyCurrentProjectChanged();
  }
}
